/* eslint-disable @next/next/no-img-element */
import React, { useCallback, useEffect, useState } from 'react'
import Button from '@/src/components/button'
import { Typography, Box, CircularProgress } from '@mui/material'
import { useRouter } from 'next/router'
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled'
import CommonApi from '@/pages/api'
import { useStore } from '@/src/types/store'
import { getYoutubeEmbedUrl } from '@/src/types/helper'

interface WelcomeSectionProps {
  sectionId: number
  isDraft?: boolean
}

const WelcomeSection = ({ sectionId, isDraft = false }: WelcomeSectionProps) => {
  const { TextColorType } = useStore()
  const router = useRouter()
  const [loading, setLoading] = useState(false)
  const [welcomeSectionData, setWelcomeSectionData] = useState<any>()
  const [showVideo, setShowVideo] = useState<boolean>(true)

  const handleImageClick = () => {
    setShowVideo(true)
  }

  const fetchWelcomeData = useCallback(async () => {
    setLoading(true)
    try {
      const response = await CommonApi({
        method: 'get',
        path: `show-welcome-content/${sectionId}${isDraft ? '?status=draft' : ''}`
      })
      if (response.status) {
        setWelcomeSectionData(response.data)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }, [sectionId, isDraft])

  useEffect(() => {
    fetchWelcomeData()
  }, [fetchWelcomeData])

  const renderVideoContent = () => {
    // YouTube flow
    if (welcomeSectionData?.media_type == 'youtube' && welcomeSectionData?.youtube_link) {
      const params = new URLSearchParams({
        modestbranding: '1',
        showinfo: '0',
        rel: '0',
        controls: '0',
        fs: '1',
        cc_load_policy: '0',
        iv_load_policy: '3',
        autohide: '1',
        disablekb: '1',
        playsinline: '1'
      })
      const embedUrl = getYoutubeEmbedUrl(welcomeSectionData.youtube_link, params)
      if (embedUrl) {
        return (
          <Box className="relative w-full aspect-[16/9] overflow-hidden rounded-[20px]">
            <iframe
              className="absolute top-0 left-0 w-full h-full rounded-[20px]"
              src={embedUrl}
              allowFullScreen
            />
          </Box>
        )
      }
    } else if (welcomeSectionData?.media_type == 'unith' && welcomeSectionData?.unith_link) {
      return (
        <Box className="relative h-[500px] rounded-[20px]">
          <iframe
            src={welcomeSectionData.unith_link}
            className="absolute top-0 left-0 w-full h-full rounded-[20px]"
            allow="microphone"
          />
        </Box>
      )
    } else if (welcomeSectionData?.media_type !== 'none') {
      // Default flow - same logic as your simplified version
      if (welcomeSectionData?.thumbnail) {
        if (!showVideo) {
          return (
            <Box className="relative">
              <PlayCircleFilledIcon
                onClick={handleImageClick}
                sx={{
                  transform: 'translate(-50%, -50%)',
                  top: '50%',
                  left: '50%'
                }}
                className="object-cover absolute w-[71px] h-[71px] text-[#FFFFFFCC]"
              />
              <img
                className="rounded-[20px] object-cover w-full max-h-[464.78px]"
                src={welcomeSectionData?.thumbnail}
                alt="Background"
              />
            </Box>
          )
        } else {
          if (welcomeSectionData?.welcome_media) {
            return (
              <Box>
                <video
                  controls
                  autoPlay
                  muted
                  className="rounded-[20px] object-contain w-full max-h-[464.78px]"
                >
                  <source src={welcomeSectionData?.welcome_media} type="video/mp4" />
                </video>
              </Box>
            )
          }
        }
      } else {
        return (
          <Box>
            <img
              className="rounded-[20px] object-cover w-full max-h-[464.78px]"
              src={welcomeSectionData?.welcome_media}
              alt="Background"
            />
          </Box>
        )
      }
    }

    return null
  }

  return (
    <Box className={`w-full flex justify-center`}>
      {loading ? (
        <Box className="w-full flex justify-center items-center h-[300px]">
          <CircularProgress size={25} />
        </Box>
      ) : (
        welcomeSectionData && (
          <Box className="w-full flex flex-col xl:flex-row justify-center gap-0 lg:gap-14">
            <Box className="w-full flex flex-col text-start py-4 rounded-[20px] gap-6">
              <Typography variant="h4" className="text-start">
                {welcomeSectionData?.title}
              </Typography>
              <Typography
                variant="body1"
                className="text-start"
                dangerouslySetInnerHTML={{
                  __html: (welcomeSectionData?.message || '').replace(/\n/g, '<br />')
                }}
              />
              <Box className="rounded-[110px] flex pt-4 gap-2 ">
                <Box>
                  <img
                    className="rounded-[50px] w-[46px] h-[48px] bg-[#ADA0FF]"
                    src={welcomeSectionData?.rep_image}
                    alt=""
                  />
                </Box>
                <Box className="flex flex-col">
                  <Typography variant="subtitle1">{welcomeSectionData?.rep_name}</Typography>
                  <Typography variant="caption">{welcomeSectionData?.rep_role}</Typography>
                </Box>
              </Box>
              <Box>
                <Button
                  classes="rounded-[100px]"
                  onClick={() => router.push('/create-account')}
                  text="Join our investor community"
                  variant="contained"
                  customStyle={true}
                  extraClass={`bg-primary-home ${
                    TextColorType.primaryTextDark ? 'text-white' : 'text-black'
                  }`}
                />
              </Box>
            </Box>
            <Box className="w-full flex flex-col lg:pt-[70px] pb-[20px]">
              {renderVideoContent()}
            </Box>
          </Box>
        )
      )}
    </Box>
  )
}

export default WelcomeSection
